import { useState } from 'react';
import { Plus, Power } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { BondStatus, mensagemDeErro } from '../api';
import { BOND_STATUS_LABELS } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useMotivos, useSalvarMotivo } from '../hooks/vidaFuncional';

const STATUSES: BondStatus[] = ['ativo', 'em_treinamento', 'suspenso', 'licenca_nao_remunerada', 'falecido'];

/** Motivos (subsituações) da situação funcional (nota da US-GP-007). */
export default function MotivosSituacaoPage() {
  const toast = useToast();
  const motivos = useMotivos('', true);
  const salvar = useSalvarMotivo();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', status: 'suspenso' });
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, ok: string) {
    try {
      await action();
      toast.success(ok);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível salvar o motivo.'));
    }
  }

  async function criar() {
    try {
      await salvar.mutateAsync({ id: null, payload: form });
      setOpen(false);
      toast.success('Motivo criado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível criar o motivo.'));
    }
  }

  return (
    <PageContainer
      title="Motivos de situação"
      subtitle="Subsituações que detalham a situação funcional. Com motivos ativos, a alteração exige um deles."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Motivos de situação' }]}
      action={
        <Can feature={FEATURES.pessoasSituacaoFuncional} action="create">
          <Button onClick={() => { setForm({ code: '', name: '', status: 'suspenso' }); setError(null); setOpen(true); }}>
            <Plus className="mr-2 h-5 w-5" /> Novo motivo
          </Button>
        </Can>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-4 py-3">Motivo</th><th className="px-4 py-3">Situação</th><th className="px-4 py-3">Ativo</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(motivos.data ?? []).map((m) => (
              <tr key={m.id} className={m.active ? '' : 'opacity-60'}>
                <td className="px-4 py-3">{m.name} <span className="font-mono text-xs text-gray-500">{m.code}</span></td>
                <td className="px-4 py-3">{BOND_STATUS_LABELS[m.status as BondStatus] ?? m.status}</td>
                <td className="px-4 py-3">{m.active ? 'Sim' : 'Não'}</td>
                <td className="px-4 py-3 text-right">
                  <Can feature={FEATURES.pessoasSituacaoFuncional} action="edit">
                    <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                      aria-label={m.active ? `Inativar ${m.name}` : `Reativar ${m.name}`}
                      onClick={() => run(() => salvar.mutateAsync({ id: m.id, payload: { active: !m.active } }), m.active ? 'Motivo inativado.' : 'Motivo reativado.')}>
                      <Power className="h-4 w-4" />
                    </button>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PessoasModal open={open} title="Novo motivo" submitLabel="Criar" isSubmitting={salvar.isPending} submitError={error}
        onClose={() => setOpen(false)} onSubmit={criar}>
        <TextInput label="Código" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))} />
        <TextInput label="Nome" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <AutocompleteSelect label="Situação" options={STATUSES.map((s) => ({ value: s, label: BOND_STATUS_LABELS[s] }))}
          value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} />
      </PessoasModal>
    </PageContainer>
  );
}
